<template>
  <div>
    <div class="card mb-3">
      <div class="card-header"><strong>{{ $t('What the consent stands for') }}</strong></div>
      <div class="card-body">
        <p v-if="frozen" class="tw:text-sm tw:text-muted-foreground">
          {{ $t('Participants have been shown this consent: its type, validation mode and channels no longer change. Create another consent to ask something else.') }}
        </p>

        <div class="form-group">
          <label for="consentType">{{ $t('Type') }}</label>
          <select
            id="consentType"
            v-model="type"
            :name="frozen ? undefined : 'type'"
            :disabled="frozen"
            :class="['form-control', { 'is-invalid': errors.type }]"
          >
            <option v-for="option in types" :key="option.value" :value="option.value">{{ option.label }}</option>
          </select>
          <div v-if="errors.type" class="invalid-feedback">{{ errors.type }}</div>
          <small v-if="selectedType" class="form-text text-muted">
            {{ selectedType.description }}
            <span class="tw:mt-1 tw:block">{{ $t('For example:') }} <em>{{ selectedType.example }}</em></span>
          </small>
          <small v-if="type === 'partner_sharing'" class="form-text text-muted">
            <template v-if="exhibitorsUrl">
              {{ $t('Without a text of your own, the consent links to the public list of the exhibitors of the event:') }}
              <a :href="exhibitorsUrl" target="_blank" rel="noopener">{{ exhibitorsUrl }}</a>
            </template>
            <template v-else>
              {{ $t('Without a text of your own, the consent links to the public list of the exhibitors of each event.') }}
            </template>
          </small>
        </div>

        <!-- Under the type, which names it until the organizer writes a name of their own. -->
        <div class="form-group">
          <label for="consentName">{{ $t('Name') }}</label>
          <input
            id="consentName"
            :value="name"
            type="text"
            name="name"
            :class="['form-control', { 'is-invalid': errors.name }]"
            @input="rename(($event.target as HTMLInputElement).value)"
          >
          <small class="form-text text-muted">{{ $t('Only seen in the back office, to find the consent in the forms.') }}</small>
          <div v-if="errors.name" class="invalid-feedback">{{ errors.name }}</div>
        </div>

        <slot />

        <div class="form-group">
          <label>{{ $t('Validation mode') }}</label>
          <div v-for="option in validationModes" :key="option.value" class="form-check">
            <input
              :id="`consentMode-${option.value}`"
              v-model="validationMode"
              type="radio"
              class="form-check-input"
              :name="frozen ? undefined : 'validation_mode'"
              :value="option.value"
              :disabled="frozen"
            >
            <label class="form-check-label" :for="`consentMode-${option.value}`">{{ option.label }}</label>
          </div>
          <p v-if="errors.validation_mode" class="tw:mt-1 tw:text-sm tw:text-red-600">{{ errors.validation_mode }}</p>
        </div>

        <div v-if="selectedType?.hasChannels" class="form-group">
          <label>{{ $t('Channels') }}</label>
          <small class="form-text text-muted mb-2">
            {{ $t('Each channel ticked gets its own box under the text. To word a text per channel, create one consent per channel.') }}
          </small>
          <div v-for="option in channels" :key="option.value" class="form-check">
            <input
              :id="`consentChannel-${option.value}`"
              v-model="selectedChannels"
              type="checkbox"
              class="form-check-input"
              :name="frozen ? undefined : 'channels[]'"
              :value="option.value"
              :disabled="frozen"
            >
            <label class="form-check-label" :for="`consentChannel-${option.value}`">{{ option.label }}</label>
          </div>
          <p v-if="errors.channels" class="tw:mt-1 tw:text-sm tw:text-red-600">{{ errors.channels }}</p>
        </div>

        <div class="form-check mb-0">
          <input type="hidden" name="is_required" value="0">
          <input
            id="consentRequired"
            v-model="isRequired"
            type="checkbox"
            class="form-check-input"
            name="is_required"
            value="1"
            :disabled="!canBeRequired"
          >
          <label class="form-check-label" for="consentRequired">{{ $t('Required to register') }}</label>
          <small v-if="!canBeRequired" class="form-text text-muted">
            {{ $t('A consent freely given cannot be required: the participant must be able to refuse it and still register.') }}
          </small>
          <p v-if="errors.is_required" class="tw:mt-1 tw:text-sm tw:text-red-600">{{ errors.is_required }}</p>
        </div>
      </div>
    </div>

    <div class="card mb-3">
      <div class="card-header"><strong>{{ $t('Texts shown to participants') }}</strong></div>
      <div class="card-body">
        <p class="tw:text-sm tw:text-muted-foreground">
          {{ $t('Word the consent in the languages of your participants. A language left empty is not offered.') }}
        </p>
        <p v-if="errors.translations" class="tw:mb-2 tw:text-sm tw:text-red-600">{{ errors.translations }}</p>

        <TranslatableForm :data-locales="locales" :data-default-locale="defaultLocale" :data-opening-locale="openingLocale">
          <template #default="{ lang }">
            <!-- Every locale stays in the form, so what is typed in a hidden tab is submitted too. -->
            <div v-for="locale in locales" v-show="lang === locale.value" :key="locale.value">
              <label :for="`consentLabel-${locale.value}`" class="tw:text-sm">{{ $t('Label next to the box') }}</label>
              <!-- The tabs sit on a grey panel: the field takes the page background, like the editor. -->
              <Input
                :id="`consentLabel-${locale.value}`"
                class="tw:bg-background"
                v-model="words[locale.value].label"
                :name="`translations[${locale.value}][label]`"
                :data-locale="locale.value"
                :aria-invalid="Boolean(errorFor(locale.value, 'label'))"
              />
              <p v-if="errorFor(locale.value, 'label')" class="tw:mt-1 tw:mb-0 tw:text-sm tw:text-red-600">{{ errorFor(locale.value, 'label') }}</p>

              <label class="tw:mt-3 tw:text-sm">{{ $t('Full text (optional)') }}</label>
              <div class="tw:mb-2">
                <div v-for="option in textSources" :key="option.value" class="form-check form-check-inline">
                  <input
                    :id="`consentTextSource-${locale.value}-${option.value}`"
                    v-model="words[locale.value].textSource"
                    type="radio"
                    class="form-check-input"
                    :value="option.value"
                  >
                  <label class="form-check-label" :for="`consentTextSource-${locale.value}-${option.value}`">{{ option.label }}</label>
                </div>
              </div>
              <!-- Only the source picked is submitted, so a link never travels with a text left behind. -->
              <template v-if="words[locale.value].textSource === 'link'">
                <Input
                  :id="`consentTextUrl-${locale.value}`"
                  class="tw:bg-background"
                  v-model="words[locale.value].text_url"
                  type="url"
                  placeholder="https://"
                  :name="`translations[${locale.value}][text_url]`"
                  :data-locale="locale.value"
                  :aria-invalid="Boolean(errorFor(locale.value, 'text_url'))"
                />
                <p v-if="errorFor(locale.value, 'text_url')" class="tw:mt-1 tw:mb-0 tw:text-sm tw:text-red-600">{{ errorFor(locale.value, 'text_url') }}</p>
                <small class="form-text text-muted">
                  {{ $t('Participants open this page from the form. Its content is yours to keep: the proof of consent records the link, not what the page says. Publish a new version when the page changes.') }}
                </small>
              </template>
              <TipTapEditor v-else :name="`translations[${locale.value}][text]`" :data-locale="locale.value" :value="words[locale.value].text" :with-media-library="false" @input="words[locale.value].text = $event" />
            </div>
          </template>
        </TranslatableForm>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, reactive, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { Input } from '@/shadcn/components/ui/input'
import TipTapEditor from '@/components/wysiwyg/TipTapEditor.vue'
import TranslatableForm from '@/components/TranslatableForm.vue'

interface Option {
  value: string
  label: string
}

interface TypeOption extends Option {
  canBeRequired: boolean
  hasChannels: boolean
  /** What the type usually covers. */
  description: string
  /** A wording the organizer could give it. */
  example: string
}

interface Locale {
  value: string
  label: string
  emoji?: string
}

interface Translation {
  label: string
  text: string | null
  text_url?: string | null
}

interface Props {
  types: TypeOption[]
  validationModes: Option[]
  channels: Option[]
  /** The languages the consent can be worded in, one tab each. */
  locales: Locale[]
  defaultLocale?: string
  initialType?: string
  /** Empty for a new consent, which is then named after its type. */
  initialName?: string
  initialValidationMode?: string
  initialChannels?: string[]
  initialRequired?: boolean
  /** The words per locale. */
  translations?: Record<string, Translation>
  /** Once a version was shown, what participants answered on is read-only. */
  frozen?: boolean
  /** Validation messages keyed by field, such as `translations.fr.label`. */
  errors?: Record<string, string>
  /** The public list of the event's exhibitors, for an event's consent. */
  exhibitorsUrl?: string
}

const props = withDefaults(defineProps<Props>(), {
  initialChannels: () => [],
  initialRequired: false,
  initialName: '',
  defaultLocale: undefined,
  translations: () => ({}),
  frozen: false,
  errors: () => ({}),
  exhibitorsUrl: undefined,
})

// A new consent comes with empty initial values from the page: it starts on the first option.
const type = ref(props.initialType || props.types[0]?.value)
const validationMode = ref(props.initialValidationMode || props.validationModes[0]?.value)
const selectedChannels = ref<string[]>([...props.initialChannels])
const isRequired = ref(props.initialRequired)

const selectedType = computed(() => props.types.find(option => option.value === type.value))
const canBeRequired = computed(() => selectedType.value?.canBeRequired ?? true)

// The consent is named after its type until the organizer writes a name of their own: changing
// the type renames it then. Emptying the field hands the name back to the type.
const typeLabel = (value: string | undefined) => props.types.find(option => option.value === value)?.label ?? ''
const name = ref(props.initialName || typeLabel(type.value))
const named = ref(props.initialName !== '' && props.initialName !== typeLabel(type.value))

function rename(value: string) {
  name.value = value
  named.value = value.trim() !== ''
}

watch(type, (value) => {
  if (!named.value) {
    name.value = typeLabel(value)
  }
})

watch(canBeRequired, (allowed) => {
  if (!allowed) {
    isRequired.value = false
  }
}, { immediate: true })

const { t } = useI18n()

const textSources = computed(() => [
  { value: 'text', label: t('Written here') },
  { value: 'link', label: t('Link to an external page') },
])

const words = reactive<Record<string, { label: string, text: string, text_url: string, textSource: 'text' | 'link' }>>(Object.fromEntries(
  props.locales.map(locale => [locale.value, {
    label: props.translations[locale.value]?.label ?? '',
    text: props.translations[locale.value]?.text ?? '',
    text_url: props.translations[locale.value]?.text_url ?? '',
    textSource: props.translations[locale.value]?.text_url ? 'link' : 'text',
  }]),
))

function errorFor(locale: string, field: keyof Translation): string | undefined {
  return props.errors[`translations.${locale}.${field}`]
}

// A form sent back with errors opens on the first language to fix; otherwise on the default
// language when it is worded, else on the first language that is, so the tab is never empty
// while a text exists.
const openingLocale = computed(() => {
  const withError = props.locales.find(
    locale => Object.keys(props.errors).some(key => key.startsWith(`translations.${locale.value}.`)),
  )
  const worded = props.locales.filter(locale => props.translations[locale.value]?.label)

  return withError?.value
    ?? worded.find(locale => locale.value === props.defaultLocale)?.value
    ?? worded[0]?.value
    ?? props.defaultLocale
})
</script>
